import { getActiveTraceHttpRequestHeaders } from '@atlaskit/react-ufo/experience-trace-id-context';

/**
 * Retry calling the given callback a few times if it throws an error that "shouldRetry" returns true for.
 */
export async function retry<Result>(
  callback: (data: { attempt: number }) => Promise<Result>,
  shouldRetry: (err: Error) => boolean = () => true,
  attempts = 3
): Promise<Result> {
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await callback({ attempt });
    } catch (err: any) {
      if (!shouldRetry(err) || attempt === attempts) {
        throw err;
      }
    }
  }
  throw new Error('Retry loop exceeded'); // Should never happen
}

/**
 * Thin wrapper around fetch API that passes in the confluence base URL
 */
export const fetchConfluenceData = async (
  path: string,
  init?: RequestInit | undefined
) => {
  const headers = new Headers(init?.headers);
  const traceHeaders = getActiveTraceHttpRequestHeaders();
  if (traceHeaders) {
    headers.set('X-B3-TraceId', traceHeaders['X-B3-TraceId']);
    headers.set('X-B3-SpanId', traceHeaders['X-B3-SpanId']);
  }
  return await fetch(`/wiki${path}`, { ...init, headers });
};

declare global {
  interface Error {
    _orderlyStatus?: number;
  }
}

/**
 * Reject a promise returned by fetch() if the response status code is an error.
 * Use like this: fetch('url').then(rejectOnError);
 */
export async function rejectOnError<T extends Response>(response: T): Promise<T> {
  if (!response.ok) {
    let body;
    try {
      body = await response.json();
    } catch (err) {
      // Ignore
    }

    const messages = [
      body?.message,
      body?.error,
      ...(body?.data?.errors?.map(
        (e: any) => e?.message?.translation ?? e?.message?.key
      ) ?? [])
    ].filter((m) => !!m);
    const message = messages.length > 0 ? messages.join(' | ') : 'Request failed';
    throw Object.assign(new Error(`${message} (${response.status}, ${response.url})`), {
      _orderlyStatus: response.status
    });
  }

  return response;
}

/**
 * Returns error status code if it exists on given err
 */
export function getErrStatus(err: unknown): number | undefined {
  if (err instanceof Error && typeof err._orderlyStatus === 'number') {
    return err._orderlyStatus;
  }
  return undefined;
}
